import { useVideoUploads } from '@/features/editor/components/shared/VideoUploadContext'
import { useEffect, useState } from 'react'
// src/features/editor/components/studio/BlockPanel.tsx
import { SquarePen } from 'lucide-react'
import type { CourseWithoutBlocks } from '@/types/course'
import { BlockEditor } from '@/features/editor/components/blocks/BlockEditor'
import { cn, ScrollArea } from '@teach/ui'
import { useIsCourseAuthor } from '@/features/editor/hooks/useIsCourseAuthor'
import { BlockRunNotice } from './BlockRunNotice'
import { useBlockDraftContext } from './BlockDraftContext'
import { useBlockIndex } from './useBlockIndex'
import { BlockPanelHeader } from './BlockPanelHeader'
import { LessonInfoPanel } from './LessonInfoPanel'
import {
  isBlockLocked,
  lockReasonOf,
  useBlocksInRun,
  useIsCourseTextLocked,
} from './useBlocksInRun'
import { useLessonPanel } from './useLessonPanel'
import { useReportEmptyPanel } from './panelSelection'
import { useBlockAutoSave } from './useBlockAutoSave'
import { useIsCourseFrozen } from '@/features/moderation/useCourseModeration'
import { liveIdentity } from '@/features/moderation/lib/liveIdentity'
import { useMessages } from '@/i18n'
import { messages } from './messages'
import { toastIfUploadLost } from './uploadLostToast'

/** Тело панели блока. Составной блок уже `lg` правится прямо на экране — тело скрыто,
 *  в шторке остаётся шапка с удалением. `display:table` ScrollArea — см. ниже. */
const panelBodyClass = (type: string): string =>
  cn(
    // Высота шторки телефона задана только через max-h, поэтому `h-full` вьюпорта Radix
    // там не срабатывает: без flex-колонки он вырастал до контента и список не листался.
    'flex min-h-0 flex-1 flex-col [&_[data-radix-scroll-area-viewport]>div]:!block [&_[data-radix-scroll-area-viewport]]:min-h-0 [&_[data-radix-scroll-area-viewport]]:flex-1',
    type === 'design' ? 'hidden lg:block' : null,
  )

interface Props {
  course: CourseWithoutBlocks
  /** Показать нечего (ни экрана, ни урока) — выезжающая панель телефона закрывается. */
  onEmpty?: () => void
}

export const BlockPanel = ({ course, onEmpty }: Props) => {
  const m = useMessages(messages)
  const videoUploads = useVideoUploads()
  const isCourseAuthor = useIsCourseAuthor(course)
  const {
    block: savedBlock,
    draft,
    setDraft,
    isDirty,
    isSaving,
    doSave,
    doDelete,
  } = useBlockDraftContext()
  // Загрузка ролика сохраняет экран сама: флаг ставится в том же событии, что и
  // правка черновика, и эффект зовёт `doSave` уже с черновиком, где ролик есть.
  const [autoSave, setAutoSave] = useState(false)
  useEffect(() => {
    if (!autoSave) return
    setAutoSave(false)
    if (isDirty) void doSave()
  }, [autoSave, isDirty, doSave])
  const blockIndex = useBlockIndex(draft?.id ?? null)
  const lessonPanel = useLessonPanel(course)
  const blocksInRun = useBlocksInRun(course.id)
  const isTextLocked = useIsCourseTextLocked(course.id)
  useReportEmptyPanel(lessonPanel.lesson, onEmpty)
  const isFrozen = useIsCourseFrozen(course.id)
  const isLocked = draft !== null && isBlockLocked(blocksInRun, draft.id)
  // `kind: 'all'` значит «заперты все» — переводом (§7.5) или проверкой курса,
  // а не «этот конкретный блок сейчас переписывается»; плашка обязана назвать
  // настоящую причину, а не соврать про правку (см. `BlockRunNotice`).
  const lockReason = lockReasonOf(blocksInRun)
  // Блок сохраняется сам на каждой паузе в правке — кнопки «Сохранить» нет.
  // Не сохраняем копию, которую сервер ещё создаёт, и блок под прогоном/проверкой.
  // Плашки конфликта с версией AI больше нет (решение заказчика): при расхождении
  // остаётся версия автора; правку во время прогона закроет будущий запрет.
  const autoSaveOn = draft !== null && draft.id > 0 && isCourseAuthor && !isLocked
  useBlockAutoSave({ draft, block: savedBlock, isDirty, doSave, enabled: autoSaveOn })

  // A selected block takes priority; otherwise a selected lesson shows its info
  // editor (ref parity); otherwise an empty hint.
  if (!draft) {
    if (lessonPanel.lesson) {
      const lessonId = lessonPanel.lesson.id
      return (
        <aside data-testid="block-panel" className="flex min-h-0 w-full flex-1 flex-col">
          {/* Прокрутку панель урока держит САМА (`LessonInfoPanel`): поля
              прокручиваются, а «Удалить урок» закреплено под ними внизу.
              Без прокрутки секция оформления (~230px) на окне 1440×900 уходила
              за границу панели, и клик по нижним карточкам не доходил. */}
          <div className="flex min-h-0 flex-1 flex-col">
            <LessonInfoPanel
              key={`${liveIdentity(lessonPanel.lesson)}-${lessonPanel.contentLang}`}
              courseId={course.id}
              lessonId={lessonPanel.lesson.id}
              lessonNumber={lessonPanel.lessonNumber}
              name={lessonPanel.name}
              description={lessonPanel.description}
              lessonTheme={lessonPanel.lesson.theme}
              savePatch={lessonPanel.savePatch}
              isCourseAuthor={isCourseAuthor}
              isTextLocked={isTextLocked}
              isFrozen={isFrozen}
              onSave={lessonPanel.save}
              onDelete={() => {
                const aborted = !isFrozen && videoUploads.abortLesson(lessonId)
                void lessonPanel.remove(isFrozen).then((removed) => {
                  toastIfUploadLost(aborted, removed, 'lesson')
                })
              }}
            />
          </div>
        </aside>
      )
    }
    return (
      <aside data-testid="block-panel" className="flex h-full w-full flex-col">
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
          <SquarePen className="h-8 w-8 text-muted-foreground/30" />
          <p className="text-sm text-muted-foreground">{m.selectBlockOrLesson}</p>
        </div>
      </aside>
    )
  }

  // Копия, которую сервер ещё создаёт (временный отрицательный id): панель видна сразу,
  // но неактивна — правка ушла бы в несуществующий блок. Через секунду приходит
  // настоящий экран, и панель оживает с тем же содержимым.
  const isPendingCopy = draft.id < 0

  // Автор подтвердил удаление — ролику больше некуда лечь. Отмена сразу, а не после ответа
  // сервера: за это время загрузка успевала кончиться и упиралась в «блока уже нет».
  // Хранилище загрузок видно отсюда, но не из `useBlockDraft`: он выше `VideoUploadProvider`.
  const removeBlock = async () => {
    const aborted = !isLocked && videoUploads.abortBlock(draft.id)
    const removed = await doDelete()
    // Ролик отменён, а блок остался: без слова автор решил бы, что загрузка идёт.
    toastIfUploadLost(aborted, removed, 'block')
  }

  return (
    <aside
      data-testid="block-panel"
      className={cn(
        'flex min-h-0 w-full flex-1 flex-col',
        isPendingCopy ? 'pointer-events-none' : null,
      )}
      // `inert` в типах React 18 нет — ставим атрибут вручную через ref.
      ref={(el) => el?.toggleAttribute('inert', isPendingCopy)}
      aria-busy={isPendingCopy || undefined}
    >
      <BlockPanelHeader
        block={draft}
        index={blockIndex}
        isDirty={isDirty}
        isSaving={isSaving}
        // Временный блок (ещё создаётся на сервере, id < 0) — тоже без ручного
        // «Сохранить»: редактор квиза на открытии дополняет тело, черновик на
        // миг «грязный», и кнопка мелькала до прихода настоящего блока.
        autoSave={autoSaveOn || (draft !== null && draft.id < 0)}
        isCourseAuthor={isCourseAuthor}
        locked={isLocked}
        lockReason={lockReason}
        onSave={doSave}
        onDelete={removeBlock}
      />
      {/* Radix ScrollArea оборачивает контент в div со style="display:table"
          (shrink-to-fit): длинный неразрывный текст (Kinescope ID) распирает его шире
          панели, и overflow-hidden Root обрезает. Принудительный display:block держит
          ширину = ширине вьюпорта, и truncate внутри работает. */}
      <ScrollArea className={panelBodyClass(draft.type)}>
        <div className="p-4">
          {isLocked ? (
            <BlockRunNotice reason={lockReason} />
          ) : (
            <>
              <BlockEditor
                block={draft}
                onChange={setDraft}
                onDelete={removeBlock}
                isCourseAuthor={isCourseAuthor}
                savedBlock={savedBlock}
                onAutoSave={() => setAutoSave(true)}
              />
            </>
          )}
        </div>
      </ScrollArea>
    </aside>
  )
}
